import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Container, Modal } from 'react-bootstrap';
import { FaCheckCircle, FaEye, FaPrint, FaTimesCircle } from 'react-icons/fa';
import { useReactToPrint } from 'react-to-print';
import CommonCardHeader from '../../CommonUtils/CommonCardHeader';
import CommonReactTable from '../../CommonUtils/CommonReactTable';
import { CommonAxiosGet } from '../../CommonUtils/CommonAxios';
import { config } from '../../CommonUtils/CommonURLS';
import { viewImage } from '../../CommonUtils/viewImage';
import StudentPreview from '../StuServices/StudentPreview';
import StudentAbstractReport from './StudentAbstractReport';

const STAGE_DEFS = [
    { id: 'submittedDetails', label: 'Student Details', keys: ['profile_status'], },
    { id: 'uploadedDocuments', label: 'Documents', keys: ['doc_upload_status'], },
    { id: 'paymentDone', label: 'Payment Done', keys: ['payment_status'], },
    { id: 'fromCollegeVerification', label: 'From College Verification (NOC)', keys: ['from_college'], },
    { id: 'toCollegeVerification', label: 'To College Verification (NOC)', keys: ['to_college'], },
    { id: 'cteAdmin', label: 'CTE Admin', keys: ['ct_admin'], },
    { id: 'superAdmin', label: 'Super Admin', keys: ['super_admin'], },
];

const SUMMARY_DEFS = [
    { id: 'totalStudents', label: 'Total Students' },
    ...STAGE_DEFS.map(({ id, label }) => ({ id, label })),
];

function toNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const asNumber = Number(value);
    return Number.isNaN(asNumber) ? null : asNumber;
}

function resolveByKeys(source, keys) {
    if (!source || typeof source !== 'object') {
        return undefined;
    }
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            return source[key];
        }
    }
    return undefined;
}

function isDoneStatus(value) {
    if (typeof value === 'boolean') { return value; }
    if (typeof value === 'number') {
        return value > 0;
    }
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
        return false;
    }
    const compact = normalized.replace(/[\s-]+/g, '_');

    const notDoneTokens = ['not_done'];
    if (notDoneTokens.includes(compact)) {
        return false;
    }
    const doneTokens = ['done'];

    return doneTokens.includes(compact);
}

function extractRows(responseData) {
    if (!responseData || typeof responseData !== 'object') {
        return [];
    }
    const directCandidates = [responseData.student_report,];

    for (let i = 0; i < directCandidates.length; i += 1) {
        if (Array.isArray(directCandidates[i])) {
            return directCandidates[i];
        }
    }

    return [];
}

function StatusIcon({ done }) {
    return done ? (
        <span className="overall-status-mark overall-status-mark--done" title="Done">
            <FaCheckCircle />
        </span>
    ) : (
        <span className="overall-status-mark overall-status-mark--not-done" title="Not Done">
            <FaTimesCircle />
        </span>
    );
}

export default function WorkflowStatusReport() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [showAbstractModal, setShowAbstractModal] = useState(false);
    const abstractPrintRef = useRef(null);

    const handleOpenPreview = (rowData) => {
        const studentId = rowData?.student_reg_no || rowData?.user_id || rowData?.candidate_id || '';
        const isStudentDetailsDone = isDoneStatus(resolveByKeys(rowData, ['profile_status']));

        if (!studentId || !isStudentDetailsDone) {
            return;
        }

        setSelectedStudentId(String(studentId));
        setShowPreviewModal(true);
    };

    const handleClosePreview = () => { setShowPreviewModal(false); setSelectedStudentId(''); };

    useEffect(() => {
        const loadReport = async () => {
            setLoading(true);
            setErrorText('');
            try {
                await CommonAxiosGet(`${config.url.STU_URL}getstudentstatusreportoverall`).then((res) => {
                    if (res?.data?.scode === '01') {
                        setRows(extractRows(res?.data));
                    } else {
                        setRows([]);
                        setErrorText(res?.data?.sdesc || 'Unable to load overall status report.');
                    }
                })
            } catch (error) {
                console.error('Error loading overall workflow status report:', error);
                setRows([]);
                setErrorText('Failed to load report data. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        loadReport();
    }, []);

    const handleOpenAbstract = () => {
        setShowAbstractModal(true);
    };

    const handleCloseAbstract = () => {
        setShowAbstractModal(false);
    };

    const handlePrintAbstract = useReactToPrint({
        contentRef: abstractPrintRef,
        documentTitle: 'StudentAbstractReport',
    });

    const summaryValues = useMemo(() => {
        const computed = STAGE_DEFS.reduce(
            (acc, stage) => {
                acc[stage.id] = rows.filter((row) => isDoneStatus(resolveByKeys(row, stage.keys))).length;
                return acc;
            },
            { totalStudents: rows.length }
        );
        const summary = {};
        SUMMARY_DEFS.forEach((item) => {
            summary[item.id] = computed[item.id];
        });
        return summary;
    }, [rows]);

    const tableColumns = useMemo(
        () => [
            {
                id: 'slNo',
                header: 'S.No',
                cell: ({ row }) => row.index + 1,
            },
            {
                id: 'studentId',
                header: 'Student ID',
                cell: ({ row }) => {
                    const studentId = row.original.student_reg_no || row.original.user_id || row.original.candidate_id || '-';
                    const isStudentDetailsDone = isDoneStatus(resolveByKeys(row.original, ['profile_status']));

                    if (!isStudentDetailsDone || studentId === '-') {
                        return studentId;
                    }

                    return (
                        <Button variant="link" className="p-0 text-decoration-underline"
                            onClick={() => handleOpenPreview(row.original)}
                            style={{ fontSize: 'inherit', lineHeight: 'inherit', color: '#0d6efd' }}
                        >
                            {studentId}
                        </Button>
                    );
                },
            },
            {
                id: 'studentName',
                header: 'Student Name',
                cell: ({ row }) => row.original.student_name || row.original.user_name || row.original.applicant_name || '-',
                Footer: 'Total',
            },
            ...STAGE_DEFS.map((stage) => ({
                id: stage.id,
                header: stage.label,
                meta: { style: { textAlign: 'center' } },
                cell: ({ row }) => {
                    const done = isDoneStatus(resolveByKeys(row.original, stage.keys));
                    const downloadPath = stage.id === 'fromCollegeVerification'
                        ? row.original.from_coll_noc
                        : stage.id === 'toCollegeVerification'
                            ? row.original.to_coll_noc
                            : '';
                    return (
                        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                            <StatusIcon done={done} />
                            {done && downloadPath ? (
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    title={stage.id === 'fromCollegeVerification' ? 'View From College NOC' : 'View To College NOC'}
                                    onClick={() => viewImage(downloadPath)}
                                >
                                    <FaEye />
                                </Button>
                            ) : null}
                        </div>
                    );
                },
                Footer: ({ table }) =>
                    table
                        .getFilteredRowModel()
                        .rows.filter((row) => isDoneStatus(resolveByKeys(row.original, stage.keys))).length,
            })),
        ],
        []
    );

    return (
        <Container fluid className="py-4">
            <Modal show={showPreviewModal} onHide={handleClosePreview} size="xl" centered backdrop="static" keyboard={false} >
                <Modal.Header closeButton>
                    <Modal.Title>Student Preview - {selectedStudentId}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {selectedStudentId && <StudentPreview studentRegNo={selectedStudentId} />}
                </Modal.Body>
            </Modal>
            <section className="workflow-section-surface overall-status-shell">
                <CommonCardHeader
                    title="Student Transfer Workflow Status"
                    actionLabel="Abstract"
                    onAction={handleOpenAbstract}
                    actionVariant="primary"
                />

                <div className="workflow-section-body">
                    {loading ? (
                        <div className="status-report-empty">Loading report...</div>
                    ) : errorText ? (
                        <div className="status-report-empty">{errorText}</div>
                    ) : (
                        <>
                            <div className="overall-status-summary-grid">
                                {SUMMARY_DEFS.map((item) => (
                                    <article key={item.id} className="overall-status-summary-card">
                                        <p className="overall-status-summary-label">{item.label}</p>
                                        <p className="overall-status-summary-value">{summaryValues[item.id] ?? 0}</p>
                                    </article>
                                ))}
                            </div>

                            <div className="status-report-table-wrap overall-status-table-wrap mt-3">
                                <CommonReactTable
                                    data={rows}
                                    columns={tableColumns}
                                    filename="StudentOverallWorkflowStatusReport"
                                    headerName="Student Transfer Overall Workflow Status"
                                    showFooter={true}
                                    showPagination={false}
                                    stickyHeader={true}
                                    stickyHeight="62vh"
                                    showExcel={false}
                                    showPrint={true}
                                    excelFileName="Student_Overall_Workflow_Status_Report"
                                />
                            </div>

                            {!rows.length && (
                                <div className="status-report-empty">No student records found.</div>
                            )}
                        </>
                    )}
                </div>
            </section>

            <Modal show={showAbstractModal} onHide={handleCloseAbstract} size="xl" centered backdrop="static" keyboard={false} >
                <Modal.Header closeButton>
                    <Modal.Title>Student Transfer Abstract Report</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    <div ref={abstractPrintRef}>
                        <StudentAbstractReport rows={rows} />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAbstract}>Close</Button>
                    <Button variant="primary" onClick={handlePrintAbstract}><FaPrint style={{ marginRight: "6px" }} /> Print</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
